import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Trophy, Clock, Gift, Sparkles } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { API_URL } from '../utils/api';

export default function RaffleDisplayPage() {
  const { sessionCode } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [spinningNumber, setSpinningNumber] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [lastWinner, setLastWinner] = useState(null);
  const [showWinnerCelebration, setShowWinnerCelebration] = useState(false);

  // Fetch session data
  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/raffle/sessions/${sessionCode}`);
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setError(null);
        
        // Check if currently drawing
        if (data.is_drawing && !isSpinning) {
          setIsSpinning(true);
          startSpinAnimation(data.first_number, data.last_number);
        } else if (!data.is_drawing && isSpinning) {
          setIsSpinning(false);
          if (data.current_spinning_number) {
            // Show winner celebration
            setSpinningNumber(data.current_spinning_number);
            const latestDraw = data.drawn_numbers[data.drawn_numbers.length - 1];
            setLastWinner(latestDraw);
            setShowWinnerCelebration(true);
            setTimeout(() => setShowWinnerCelebration(false), 5000);
          }
        }
      } else if (response.status === 404) {
        setError('Session not found');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  }, [sessionCode, isSpinning]);

  // Spinning animation
  const startSpinAnimation = (min, max) => {
    let count = 0;
    const maxSpins = 30;
    const interval = setInterval(() => {
      const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
      setSpinningNumber(randomNum);
      count++;
      if (count >= maxSpins) {
        clearInterval(interval);
      }
    }, 80);
  };

  // Poll for updates every 1 second
  useEffect(() => {
    fetchSession();
    const interval = setInterval(fetchSession, 1000);
    return () => clearInterval(interval);
  }, [fetchSession]);

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 flex items-center justify-center">
        <Ticket className="w-20 h-20 text-amber-400 animate-pulse" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Ticket className="w-24 h-24 text-slate-600 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-2">Raffle Not Available</h1>
          <p className="text-xl text-slate-400">{error || 'Session not found'}</p>
        </div>
      </div>
    );
  }

  const totalTickets = session.last_number - session.first_number + 1;
  const drawnCount = session.drawn_numbers.length;
  const remainingCount = totalTickets - drawnCount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900/20 to-slate-900 overflow-hidden">
      {/* Winner Celebration Overlay */}
      <AnimatePresence>
        {showWinnerCelebration && lastWinner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: "spring", damping: 15 }}
              className="text-center"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ repeat: Infinity, duration: 0.5 }}
                className="mb-8"
              >
                <Trophy className="w-32 h-32 text-amber-400 mx-auto" />
              </motion.div>
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <p className="text-3xl text-amber-300 font-semibold mb-4">{lastWinner.prize_label}</p>
                <div className="text-[12rem] font-black text-white leading-none drop-shadow-2xl">
                  {lastWinner.number}
                </div>
              </motion.div>
              {/* Sparkles animation */}
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  initial={{ 
                    x: 0, 
                    y: 0, 
                    opacity: 1,
                    scale: 0
                  }}
                  animate={{ 
                    x: (Math.random() - 0.5) * 600,
                    y: (Math.random() - 0.5) * 600,
                    opacity: 0,
                    scale: Math.random() * 2 + 1
                  }}
                  transition={{ 
                    duration: 2,
                    delay: Math.random() * 0.5,
                    repeat: Infinity
                  }}
                  style={{
                    left: '50%',
                    top: '50%',
                  }}
                >
                  <Sparkles className="w-8 h-8 text-amber-400" />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="p-6 flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <Ticket className="w-10 h-10 text-amber-400" />
            Raffle Draw
          </h1>
          <p className="text-xl text-slate-400 mt-2">Hosted by {session.host_name}</p>
        </div>
        <div className="text-right">
          <div className="text-5xl font-mono text-white">
            {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-slate-400 flex items-center justify-end gap-2 mt-2">
            <Clock className="w-5 h-5" />
            {currentTime.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-180px)]">
        {/* Left: Current Number Display */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AnimatePresence mode="wait">
              {isSpinning ? (
                <motion.div
                  key="spinning"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative"
                >
                  <motion.div
                    animate={{ 
                      scale: [1, 1.05, 1],
                      textShadow: [
                        "0 0 20px rgba(251, 191, 36, 0.5)",
                        "0 0 60px rgba(251, 191, 36, 0.8)",
                        "0 0 20px rgba(251, 191, 36, 0.5)"
                      ]
                    }}
                    transition={{ repeat: Infinity, duration: 0.3 }}
                    className="text-[16rem] font-black text-amber-400 leading-none"
                  >
                    {spinningNumber || '?'}
                  </motion.div>
                  <p className="text-3xl text-amber-300 mt-4 animate-pulse">Drawing...</p>
                </motion.div>
              ) : session.current_spinning_number ? (
                <motion.div
                  key="result"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", damping: 10 }}
                >
                  <div className="text-[16rem] font-black text-white leading-none drop-shadow-2xl">
                    {session.current_spinning_number}
                  </div>
                  {session.drawn_numbers.length > 0 && (
                    <p className="text-3xl text-amber-300 mt-4">
                      {session.drawn_numbers[session.drawn_numbers.length - 1]?.prize_label}
                    </p>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="waiting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  <Gift className="w-40 h-40 text-amber-400/50 mx-auto mb-8" />
                  <p className="text-4xl text-slate-400">Waiting for draw...</p>
                  <p className="text-2xl text-slate-400 mt-4">
                    Tickets {session.first_number} - {session.last_number}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right: Stats and History */}
        <div className="w-96 bg-slate-800/50 p-6 flex flex-col">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-700/50 rounded-xl p-4 text-center">
              <div className="text-4xl font-bold text-white">{totalTickets}</div>
              <div className="text-slate-400 text-sm">Total Tickets</div>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-4 text-center">
              <div className="text-4xl font-bold text-amber-400">{remainingCount}</div>
              <div className="text-slate-400 text-sm">Remaining</div>
            </div>
          </div>

          {/* Ticket Range */}
          <div className="bg-slate-700/30 rounded-xl p-4 mb-6">
            <div className="text-slate-400 text-sm mb-2">Ticket Range</div>
            <div className="text-2xl font-bold text-white">
              {session.first_number} - {session.last_number}
            </div>
          </div>

          {/* Drawn Numbers History */}
          <div className="flex-1 overflow-hidden">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Winners ({drawnCount})
            </h3>
            <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-500px)]">
              {[...session.drawn_numbers].reverse().map((draw, index) => (
                <motion.div
                  key={draw.number}
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index === 0 ? 'bg-amber-500/20 border border-amber-500/50' : 'bg-slate-700/30'
                  }`}
                >
                  <span className={`font-semibold ${index === 0 ? 'text-amber-300' : 'text-slate-400'}`}>
                    {draw.prize_label}
                  </span>
                  <span className={`text-2xl font-bold ${index === 0 ? 'text-amber-400' : 'text-white'}`}>
                    {draw.number}
                  </span>
                </motion.div>
              ))}
              {drawnCount === 0 && (
                <div className="text-center text-slate-400 py-8">
                  No numbers drawn yet
                </div>
              )}
            </div>
          </div>

          {/* Session Code */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <div className="text-center">
              <div className="text-slate-400 text-sm mb-1">Session Code</div>
              <div className="text-2xl font-mono font-bold text-amber-400">{session.session_code}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
